import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import PaletteIcon from "@material-ui/icons/Palette";
import GradientIcon from "@material-ui/icons/Gradient";
import RgbaColorPickerPopover from "./RgbaColorPickerPopover";

const isGradient = (v) => v && String(v).trim().startsWith("linear-gradient");
const parseGradient = (str) => {
	if (!isGradient(str)) return null;
	const m = str.match(/linear-gradient\s*\(\s*(\d+)\s*deg\s*,\s*(rgba?\([^)]+\))\s*\d+%\s*,\s*(rgba?\([^)]+\))\s*\d+%\s*\)/);
	if (m) return { angle: +m[1], color1: m[2].trim(), color2: m[3].trim() };
	const m2 = str.match(/linear-gradient\s*\(\s*(\d+)\s*deg\s*,\s*(#[0-9A-Fa-f]{3,8})\s*\d+%\s*,\s*(#[0-9A-Fa-f]{3,8})\s*\d+%\s*\)/);
	if (m2) return { angle: +m2[1], color1: m2[2], color2: m2[3] };
	return { angle: 135, color1: "rgba(100,100,255,0.8)", color2: "rgba(200,100,255,0.5)" };
};
const toGradient = (angle, color1, color2) =>
	`linear-gradient(${angle}deg, ${color1} 0%, ${color2} 100%)`;

export default function ShapeColorPicker({ value, onChange, label }) {
	const [mode, setMode] = useState(isGradient(value) ? "gradient" : "flat");
	const [gradAngle, setGradAngle] = useState(135);
	const [gradColor1, setGradColor1] = useState("rgba(100,100,255,0.8)");
	const [gradColor2, setGradColor2] = useState("rgba(200,100,255,0.5)");
	const debounceRef = useRef(null);
	const isExternalUpdate = useRef(false);

	useEffect(() => {
		if (isExternalUpdate.current) {
			isExternalUpdate.current = false;
			return;
		}
		if (isGradient(value)) {
			const p = parseGradient(value);
			if (p) {
				setMode("gradient");
				setGradAngle(p.angle);
				setGradColor1(p.color1);
				setGradColor2(p.color2);
			}
		} else {
			setMode("flat");
		}
	}, [value]);

	const notifyChange = useCallback((nextValue) => {
		isExternalUpdate.current = true;
		onChange(nextValue);
	}, [onChange]);

	const debouncedNotify = useCallback((nextValue) => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			debounceRef.current = null;
			notifyChange(nextValue);
		}, 80);
	}, [notifyChange]);

	useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

	const handleModeChange = (e, newMode) => {
		if (!newMode) return;
		if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
		setMode(newMode);
		if (newMode === "flat") {
			notifyChange(isGradient(value) ? gradColor1 : (value || "rgba(100,100,255,0.8)"));
		} else {
			notifyChange(toGradient(gradAngle, gradColor1, gradColor2));
		}
	};

	const handleFlatChange = (color) => notifyChange(color);
	const gradRef = useRef({ angle: gradAngle, color1: gradColor1, color2: gradColor2 });
	gradRef.current = { angle: gradAngle, color1: gradColor1, color2: gradColor2 };
	const handleGradColor1 = useCallback((c) => {
		setGradColor1(c);
		const { angle, color2 } = gradRef.current;
		debouncedNotify(toGradient(angle, c, color2));
	}, [debouncedNotify]);
	const handleGradColor2 = useCallback((c) => {
		setGradColor2(c);
		const { angle, color1 } = gradRef.current;
		debouncedNotify(toGradient(angle, color1, c));
	}, [debouncedNotify]);
	const handleGradAngle = useCallback((a) => {
		setGradAngle(a);
		const { color1, color2 } = gradRef.current;
		debouncedNotify(toGradient(a, color1, color2));
	}, [debouncedNotify]);

	const displayValue = mode === "flat" ? (value || "rgba(100,100,255,0.8)") : toGradient(gradAngle, gradColor1, gradColor2);

	return (
		<Box>
			{label && (
				<Box fontSize={12} fontWeight={600} color="#334155" mb={0.75}>{label}</Box>
			)}
			<ToggleButtonGroup
				value={mode}
				exclusive
				onChange={handleModeChange}
				size="small"
				style={{ marginBottom: 8 }}
			>
				<ToggleButton value="flat" style={{ textTransform: "none", fontSize: 11 }}>
					<PaletteIcon style={{ fontSize: 14, marginRight: 4 }} />
					Color plano
				</ToggleButton>
				<ToggleButton value="gradient" style={{ textTransform: "none", fontSize: 11 }}>
					<GradientIcon style={{ fontSize: 14, marginRight: 4 }} />
					Gradiente
				</ToggleButton>
			</ToggleButtonGroup>
			{mode === "flat" ? (
				<RgbaColorPickerPopover value={value || "rgba(100,100,255,0.8)"} onChange={handleFlatChange} />
			) : (
				<Box display="flex" flexDirection="column" style={{ gap: 10 }}>
					<Box display="flex" alignItems="center" style={{ gap: 8 }}>
						<Box fontSize={11} color="#64748b" style={{ minWidth: 36 }}>Ángulo</Box>
						<input
							type="range"
							min={0}
							max={360}
							value={gradAngle}
							onChange={(e) => handleGradAngle(Number(e.target.value))}
							style={{ flex: 1, accentColor: "#7c3aed" }}
						/>
						<Box fontSize={11} color="#64748b" style={{ minWidth: 28 }}>{gradAngle}°</Box>
					</Box>
					<Box display="flex" flexDirection="column" style={{ gap: 8 }}>
						<RgbaColorPickerPopover label="Color 1" value={gradColor1} onChange={handleGradColor1} />
						<RgbaColorPickerPopover label="Color 2" value={gradColor2} onChange={handleGradColor2} />
					</Box>
					<Box
						style={{
							height: 28,
							borderRadius: 8,
							background: displayValue,
							border: "1px solid #e2e8f0",
						}}
					/>
				</Box>
			)}
		</Box>
	);
}
